import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Budget, BudgetListMeta, BudgetStatus, BudgetServiceLine, Client, Service } from "../../../types";
import { budgetsApi, clientsApi, servicesApi, companyApi } from "../../../services";
import { useCompany } from "../../../contexts/CompanyContext";
import {
  Card,
  ConfirmationModal,
  EmptyState,
  ErrorState,
  Loading,
  useToaster,
} from "../../shared";
import { Select } from "../../shared/Select";
import { Icons } from "../../shared/Icons";
import { AdminPageHeader } from "../shared";
import { BudgetsListView } from "./BudgetsListView";
import { BudgetPagination } from "./BudgetPagination";
import { CreateBudgetModal } from "./CreateBudgetModal";
import { CompleteBudgetModal } from "./CompleteBudgetModal";
import { CreateAppointmentModal } from "../AdminAppointments/CreateAppointmentModal";
import { DownloadBudgetPdfModal } from "./DownloadBudgetPdfModal";
import type { BudgetPdfPriceOptions } from "./DownloadBudgetPdfModal";
import { downloadBudgetPdf } from "../../../utils/budgetPdf";
import { getTodayInBrazil } from "../../../utils/dateUtils";
import {
  buildAppointmentPrefillFromBudgetLine,
  type BudgetAppointmentPrefill,
} from "../../../utils/budgetToAppointmentUtils";
import {
  getBudgetLineLabel,
  getBudgetLineSubServiceLabels,
  getBudgetServices,
  parseBudgetPrice,
  resolveBudgetTotal,
} from "../../../utils/budgetUtils";
import {
  getBudgetWhatsAppMessage,
  sendWhatsAppMessage,
} from "../../../utils/whatsappUtils";

const PAGE_SIZE = 10;

interface BudgetAppointmentQueue {
  budget: Budget;
  lines: BudgetServiceLine[];
  nextIndex: number;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Abertos e concluídos" },
  { value: "Created", label: "Abertos" },
  { value: "Completed", label: "Concluídos" },
  { value: "Cancelled", label: "Cancelados" },
];

export const AdminBudgets: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const { showToast } = useToaster();
  const isCarCompany = company?.category === "car";

  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [meta, setMeta] = useState<BudgetListMeta | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [clientFilter, setClientFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [newBudgetClientId, setNewBudgetClientId] = useState<string | undefined>();

  const [budgetToCancel, setBudgetToCancel] = useState<Budget | null>(null);
  const [budgetToComplete, setBudgetToComplete] = useState<Budget | null>(null);
  const [budgetToDownloadPdf, setBudgetToDownloadPdf] = useState<Budget | null>(
    null
  );
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);
  const [appointmentQueue, setAppointmentQueue] =
    useState<BudgetAppointmentQueue | null>(null);
  const [appointmentPrefill, setAppointmentPrefill] =
    useState<BudgetAppointmentPrefill | null>(null);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (company && !isCarCompany) {
      navigate("/admin/daily-cash", { replace: true });
    }
  }, [company, isCarCompany, navigate]);

  const loadReferenceData = useCallback(async () => {
    const [clientsRes, servicesRes] = await Promise.all([
      clientsApi.getClientsByCompany(),
      servicesApi.getServicesByCompany(),
    ]);
    if (clientsRes.success && clientsRes.data) {
      setClients(clientsRes.data);
    }
    if (servicesRes.success && servicesRes.data) {
      setServices(servicesRes.data);
    }
  }, []);

  const loadBudgets = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await budgetsApi.getBudgets({
        page,
        limit: PAGE_SIZE,
        clientId: clientFilter ? Number(clientFilter) : undefined,
        status: (statusFilter as BudgetStatus) || undefined,
      });

      if (res.success && res.data) {
        setBudgets(res.data);
        setMeta(res.meta ?? null);
      } else {
        setBudgets([]);
        setMeta(null);
        setError(res.error || "Erro ao carregar orçamentos");
      }
    } catch (e) {
      setBudgets([]);
      setMeta(null);
      setError(
        e instanceof Error ? e.message : "Erro ao carregar orçamentos"
      );
    } finally {
      setLoading(false);
    }
  }, [page, clientFilter, statusFilter]);

  useEffect(() => {
    if (company?.id && isCarCompany) {
      loadReferenceData();
    }
  }, [company?.id, isCarCompany, loadReferenceData]);

  useEffect(() => {
    if (isCarCompany && company?.id) {
      loadBudgets();
    }
  }, [isCarCompany, company?.id, loadBudgets]);

  useEffect(() => {
    setPage(1);
  }, [clientFilter, statusFilter]);

  useEffect(() => {
    if (!loading && meta && page > meta.totalPages) {
      setPage(meta.totalPages);
    }
  }, [loading, meta, page]);

  const handleCreate = () => {
    setEditingBudget(null);
    setNewBudgetClientId(clientFilter || undefined);
    setModalOpen(true);
  };

  const handleEdit = async (budget: Budget) => {
    const res = await budgetsApi.getBudgetById(budget.id);
    if (res.success && res.data) {
      setEditingBudget(res.data);
      setModalOpen(true);
    } else {
      showToast("error", res.error || "Erro ao carregar orçamento");
    }
  };

  const handleModalSuccess = () => {
    const wasEditing = Boolean(editingBudget);
    setModalOpen(false);
    setEditingBudget(null);
    showToast(
      "success",
      wasEditing ? "Orçamento atualizado" : "Orçamento criado"
    );
    loadBudgets();
  };

  const handleCancelConfirm = async () => {
    if (!budgetToCancel) return;
    setActionLoading(true);
    try {
      const res = await budgetsApi.cancelBudget(budgetToCancel.id);
      if (res.success) {
        showToast("success", "Orçamento cancelado");
        setBudgetToCancel(null);
        loadBudgets();
      } else {
        showToast("error", res.error || "Erro ao cancelar orçamento");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteConfirm = async () => {
    if (!budgetToComplete) return;
    setActionLoading(true);
    try {
      const res = await budgetsApi.updateBudget(budgetToComplete.id, {
        status: "Completed",
      });
      if (res.success) {
        showToast("success", "Orçamento concluído");
        setBudgetToComplete(null);
        loadBudgets();
      } else {
        showToast("error", res.error || "Erro ao concluir orçamento");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const openAppointmentFromBudget = (
    budget: Budget,
    line: BudgetServiceLine
  ) => {
    setAppointmentPrefill(buildAppointmentPrefillFromBudgetLine(budget, line));
    setAppointmentModalOpen(true);
  };

  const startAppointmentFlow = async (budget: Budget) => {
    const res = await budgetsApi.getBudgetById(budget.id);
    const full = res.success && res.data ? res.data : budget;

    const lines = getBudgetServices(full);
    if (lines.length === 0) {
      showToast("error", "Orçamento sem serviços para agendar");
      return;
    }

    if (lines.length > 1) {
      setAppointmentQueue({ budget: full, lines, nextIndex: 0 });
    } else {
      setAppointmentQueue(null);
    }

    openAppointmentFromBudget(full, lines[0]);
  };

  const clearAppointmentFlow = () => {
    setAppointmentModalOpen(false);
    setAppointmentPrefill(null);
    setAppointmentQueue(null);
  };

  const handleAppointmentClose = () => {
    if (
      appointmentQueue &&
      appointmentQueue.nextIndex < appointmentQueue.lines.length - 1
    ) {
      const remaining =
        appointmentQueue.lines.length - appointmentQueue.nextIndex - 1;
      const confirmed = window.confirm(
        `Ainda restam ${remaining} serviço(s) para agendar neste orçamento. Deseja cancelar os agendamentos restantes?`
      );
      if (!confirmed) return;
    }
    clearAppointmentFlow();
  };

  const handleSkipRemainingAppointments = () => {
    clearAppointmentFlow();
    showToast("success", "Agendamentos restantes ignorados");
  };

  const handleCompleteAndSchedule = async () => {
    if (!budgetToComplete) return;
    setActionLoading(true);
    try {
      const res = await budgetsApi.updateBudget(budgetToComplete.id, {
        status: "Completed",
      });
      if (!res.success) {
        showToast("error", res.error || "Erro ao concluir orçamento");
        return;
      }

      const completedBudget = budgetToComplete;
      setBudgetToComplete(null);
      loadBudgets();
      showToast("success", "Orçamento concluído");
      await startAppointmentFlow(completedBudget);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAppointmentSuccess = () => {
    setAppointmentModalOpen(false);
    setAppointmentPrefill(null);

    if (!appointmentQueue) {
      showToast("success", "Agendamento criado");
      return;
    }

    const { budget, lines, nextIndex } = appointmentQueue;
    const completedCount = nextIndex + 1;

    if (completedCount < lines.length) {
      showToast(
        "success",
        `Agendamento ${completedCount} de ${lines.length} criado`
      );
      const nextQueueIndex = nextIndex + 1;
      setAppointmentQueue({ budget, lines, nextIndex: nextQueueIndex });
      setAppointmentPrefill(
        buildAppointmentPrefillFromBudgetLine(budget, lines[nextQueueIndex])
      );
      setAppointmentModalOpen(true);
      return;
    }

    setAppointmentQueue(null);
    showToast("success", "Todos os agendamentos do orçamento foram criados");
  };

  const handleDownloadPdf = (budget: Budget) => {
    setBudgetToDownloadPdf(budget);
  };

  const handleDownloadPdfConfirm = async (options: BudgetPdfPriceOptions) => {
    if (!budgetToDownloadPdf) return;

    setPdfDownloadLoading(true);
    try {
      // Fetch full budget + company so PDF header (CNPJ, Instagram, logo, signature) is up to date
      const [budgetRes, companyRes] = await Promise.all([
        budgetsApi.getBudgetById(budgetToDownloadPdf.id),
        companyApi.getCompany(),
      ]);
      const full =
        budgetRes.success && budgetRes.data
          ? budgetRes.data
          : budgetToDownloadPdf;
      const companyForPdf =
        companyRes.success && companyRes.data ? companyRes.data : company;
      await downloadBudgetPdf(full, companyForPdf, options);
      setBudgetToDownloadPdf(null);
    } catch (e) {
      showToast(
        "error",
        e instanceof Error ? e.message : "Erro ao gerar PDF do orçamento"
      );
    } finally {
      setPdfDownloadLoading(false);
    }
  };

  const handleWhatsApp = async (budget: Budget) => {
    let full = budget;
    if (!budget.client?.phoneNumber) {
      const res = await budgetsApi.getBudgetById(budget.id);
      if (res.success && res.data) {
        full = res.data;
      }
    }

    const phone = full.client?.phoneNumber;
    if (!phone?.trim()) {
      showToast("error", "Cliente sem telefone cadastrado");
      return;
    }

    const servicesList = getBudgetServices(full).map((line) => ({
      name: getBudgetLineLabel(line),
      price: parseBudgetPrice(line.price),
      subServices: getBudgetLineSubServiceLabels(line),
    }));

    const carLabel = full.clientCar
      ? `${full.clientCar.model} · ${full.clientCar.plate}`
      : undefined;

    sendWhatsAppMessage({
      phoneNumber: phone,
      message: getBudgetWhatsAppMessage({
        clientName: full.client?.name ?? "Cliente",
        companyName: company?.name ?? "Empresa",
        budgetId: full.id,
        services: servicesList,
        total: resolveBudgetTotal(full),
        createdAt: full.createdAt,
        carLabel,
        isPaid: full.status === "Completed",
      }),
    });
  };

  const totalPages = meta?.totalPages ?? 1;
  const totalCount = meta?.total ?? budgets.length;
  const currentPage = meta?.page ?? page;

  const clientOptions = [
    { value: "", label: "Todos os clientes" },
    ...clients.map((c) => ({ value: String(c.id), label: c.name })),
  ];

  if (!isCarCompany) {
    return null;
  }

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.Money sx={{ fontSize: 28 }} />}
        title="Orçamentos"
        subtitle={
          loading
            ? "Carregando orçamentos…"
            : `${totalCount} orçamento${totalCount !== 1 ? "s" : ""}`
        }
        buttonText="Novo Orçamento"
        buttonIcon={<Icons.Add sx={{ fontSize: 20 }} />}
        onButtonClick={handleCreate}
        midleContent={
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Select
              value={clientFilter}
              onChange={(v) => setClientFilter(String(v))}
              options={clientOptions}
              placeholder="Filtrar por cliente"
              searchable
              className="min-w-[12rem]"
            />
            <Select
              value={statusFilter}
              onChange={(v) => setStatusFilter(String(v))}
              options={STATUS_OPTIONS}
              placeholder="Status"
              searchable={false}
              className="min-w-[10rem]"
            />
          </div>
        }
      />

      {loading && (
        <Card className="p-8 border border-gray-100 shadow-sm">
          <Loading message="Carregando orçamentos…" size="md" className="py-8" />
        </Card>
      )}

      {!loading && error && (
        <Card className="p-6 border border-gray-100 shadow-sm">
          <ErrorState
            message={error}
            variant="inline"
            onRetry={loadBudgets}
            className="py-4"
          />
        </Card>
      )}

      {!loading && !error && budgets.length === 0 && (
        <Card className="p-8 border border-gray-100 shadow-sm">
          <EmptyState
            icon={<Icons.Money sx={{ fontSize: 56 }} />}
            title="Nenhum orçamento encontrado"
            description="Crie um novo orçamento ou ajuste os filtros."
            actionText="Novo Orçamento"
            onAction={handleCreate}
          />
        </Card>
      )}

      {!loading && !error && budgets.length > 0 && (
        <>
          <BudgetsListView
            budgets={budgets}
            onEdit={handleEdit}
            onCancel={setBudgetToCancel}
            onComplete={setBudgetToComplete}
            onDownloadPdf={handleDownloadPdf}
            onWhatsApp={handleWhatsApp}
          />

          <BudgetPagination
            page={currentPage}
            totalPages={totalPages}
            total={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            disabled={loading}
          />
        </>
      )}

      {modalOpen && (
        <CreateBudgetModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingBudget(null);
          }}
          onSuccess={handleModalSuccess}
          clients={clients}
          services={services}
          editingBudget={editingBudget}
          initialClientId={editingBudget ? undefined : newBudgetClientId}
        />
      )}

      <ConfirmationModal
        isOpen={Boolean(budgetToCancel)}
        title="Cancelar Orçamento"
        message={`Tem certeza que deseja cancelar o orçamento #${budgetToCancel?.id} de ${budgetToCancel?.client?.name}?`}
        confirmText="Cancelar Orçamento"
        cancelText="Voltar"
        loading={actionLoading}
        onConfirm={handleCancelConfirm}
        onCancel={() => setBudgetToCancel(null)}
      />

      <CompleteBudgetModal
        isOpen={Boolean(budgetToComplete)}
        budgetLabel={`o orçamento #${budgetToComplete?.id} de ${budgetToComplete?.client?.name ?? "cliente"}`}
        loading={actionLoading}
        onCancel={() => setBudgetToComplete(null)}
        onComplete={handleCompleteConfirm}
        onCompleteAndSchedule={handleCompleteAndSchedule}
      />

      <DownloadBudgetPdfModal
        isOpen={Boolean(budgetToDownloadPdf)}
        budgetLabel={`orçamento #${budgetToDownloadPdf?.id} de ${budgetToDownloadPdf?.client?.name ?? "cliente"}`}
        loading={pdfDownloadLoading}
        onClose={() => {
          if (!pdfDownloadLoading) setBudgetToDownloadPdf(null);
        }}
        onConfirm={handleDownloadPdfConfirm}
      />

      {appointmentModalOpen && appointmentPrefill && (
        <CreateAppointmentModal
          isOpen={appointmentModalOpen}
          onClose={handleAppointmentClose}
          onSuccess={handleAppointmentSuccess}
          selectedDate={getTodayInBrazil()}
          budgetQueueProgress={
            appointmentQueue && appointmentQueue.lines.length > 1
              ? {
                  current: appointmentQueue.nextIndex + 1,
                  total: appointmentQueue.lines.length,
                }
              : undefined
          }
          onSkipRemaining={
            appointmentQueue &&
            appointmentQueue.nextIndex < appointmentQueue.lines.length - 1
              ? handleSkipRemainingAppointments
              : undefined
          }
          preFilledData={{
            clientId: appointmentPrefill.clientId,
            serviceId: appointmentPrefill.serviceId,
            clientCarId: appointmentPrefill.clientCarId,
            price: appointmentPrefill.price,
            subServiceIds: appointmentPrefill.subServiceIds,
            lockPrice: appointmentPrefill.lockPrice,
            budgetId: appointmentPrefill.budgetId,
            date: getTodayInBrazil(),
          }}
        />
      )}
    </div>
  );
};
